import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api, fileUrl } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import FeedCard from '@/components/FeedCard';
import ProfileBackground from '@/components/ProfileBackground';
import ProfileLibraryStrip from '@/components/ProfileLibraryStrip';
import AudioPlayer from '@/components/AudioPlayer';
import { sanitizeBioHtml } from '@/lib/sanitize';
import { UserPlus, UserMinus, MessageCircle, UserCheck, Settings as Cog, Users, Heart, Ban, ShieldAlert, Folder } from 'lucide-react';
import { toast } from 'sonner';

const WALL_KINDS = ['main', 'creator', 'investor', 'crowdfunder', 'dating', 'marketplace'];

export default function ProfilePage({ self = false }) {
  const { username: routeUsername } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const username = self ? user?.username : routeUsername;
  const initialWall = (() => {
    const w = (searchParams.get('wall') || 'main').toLowerCase();
    return WALL_KINDS.includes(w) ? w : 'main';
  })();
  const [wall, setWall] = useState(initialWall);
  const [data, setData] = useState(null);
  const [customizations, setCustomizations] = useState({});
  const [followStatus, setFollowStatus] = useState({ following: false, follows_me: false });
  const [friendStatus, setFriendStatus] = useState({ status: 'none' });
  const [counts, setCounts] = useState({ followers: 0, following: 0, friends: 0 });
  const [tab, setTab] = useState('posts');
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [busy, setBusy] = useState(false);
  const [blockState, setBlockState] = useState({ i_blocked_them: false, they_blocked_me: false });

  const isMe = user && username && user.username === username.toLowerCase();
  // When a user opens their own profile with `?preview=1`, render the page as
  // a visitor would see it (no edit/customize controls). They can exit via the
  // "Exit preview" banner. This is invoked from ProfileEdit's "Preview as
  // visitor" button.
  const previewMode = isMe && (searchParams.get('preview') === '1');
  const showAsOwner = isMe && !previewMode;
  // The active customization follows the active wall tab — each wall has its own scope.
  const customization = customizations[wall] || customizations.main || null;

  // Group posts by wall_kind once per `data` change so each TabsContent does
  // not re-filter on every render. Also lets us memoize emptiness checks.
  const postsByWall = useMemo(() => {
    const groups = Object.fromEntries(WALL_KINDS.map((k) => [k, []]));
    for (const p of (data?.posts || [])) {
      if (groups[p.wall_kind]) groups[p.wall_kind].push(p);
    }
    return groups;
  }, [data]);

  const load = useCallback(async () => {
    if (!username) return;
    try {
      const [wallRes, ...customs] = await Promise.all([
        api.get(`/users/${username}/walls/${wall}`),
        ...WALL_KINDS.map((k) => api.get(`/profile/customization/${username}`, { params: { scope: k } })),
      ]);
      const followersRes = await api.get(`/follows/${username}/followers`);
      const followingRes = await api.get(`/follows/${username}/following`);
      setData(wallRes.data);
      const cmap = {};
      WALL_KINDS.forEach((k, i) => { cmap[k] = customs[i]?.data || {}; });
      setCustomizations(cmap);
      setFollowers(followersRes.data || []);
      setFollowing(followingRes.data || []);
      setCounts((c) => ({ ...c, followers: followersRes.data?.length || 0, following: followingRes.data?.length || 0 }));
    } catch (e) { toast.error('Could not load profile'); }
    if (!isMe && user?.id) {
      try {
        const [fol, fr, bl] = await Promise.all([
          api.get(`/follows/status/${username}`),
          api.get(`/friends/status/${username}`),
          api.get(`/blocks/check`, { params: { username } }).catch(() => ({ data: { i_blocked_them: false, they_blocked_me: false } })),
        ]);
        setFollowStatus(fol.data); setFriendStatus(fr.data); setBlockState(bl.data || { i_blocked_them: false, they_blocked_me: false });
      } catch {}
    }
  }, [username, wall, isMe, user?.id]);

  useEffect(() => { load(); }, [load]);

  // Keep ?wall= in sync with the active tab (for shareable / previewable URLs).
  useEffect(() => {
    const cur = searchParams.get('wall') || 'main';
    if (cur !== wall) {
      const sp = new URLSearchParams(searchParams);
      if (wall === 'main') sp.delete('wall'); else sp.set('wall', wall);
      setSearchParams(sp, { replace: true });
    }
  }, [wall]); // eslint-disable-line

  const requestRole = async (role) => { setBusy(true); try { await api.post('/users/me/roles/request', null, { params: { role } }); toast.success(`Granted ${role}`); window.location.reload(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setBusy(false); } };
  const toggleFollow = async () => {
    setBusy(true);
    try {
      if (followStatus.following) { await api.delete(`/follows/${username}`); setFollowStatus((s) => ({ ...s, following: false })); setCounts((c) => ({ ...c, followers: Math.max(0, c.followers - 1) })); toast.success('Unfollowed'); }
      else { await api.post(`/follows/${username}`); setFollowStatus((s) => ({ ...s, following: true })); setCounts((c) => ({ ...c, followers: c.followers + 1 })); toast.success('Followed'); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  const friendAction = async () => {
    setBusy(true);
    try {
      if (friendStatus.status === 'none') { await api.post(`/friends/request/${username}`); setFriendStatus({ status: 'pending', i_requested: true }); toast.success('Friend request sent'); }
      else if (friendStatus.status === 'pending' && !friendStatus.i_requested) { await api.post(`/friends/accept/${username}`); setFriendStatus({ status: 'accepted' }); toast.success('Friend request accepted'); }
      else if (friendStatus.status === 'accepted') { await api.delete(`/friends/${username}`); setFriendStatus({ status: 'none' }); toast.success('Removed friend'); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  const toggleBlock = async () => {
    if (!username || isMe) return;
    setBusy(true);
    try {
      if (blockState.i_blocked_them) {
        await api.delete(`/blocks/${username}`);
        setBlockState((s) => ({ ...s, i_blocked_them: false }));
        toast.success(`Unblocked @${username}`);
      } else {
        if (!window.confirm(`Block @${username}? You won't see their content and they can't message you.`)) {
          setBusy(false);
          return;
        }
        await api.post('/blocks', { username });
        setBlockState((s) => ({ ...s, i_blocked_them: true }));
        // Drop any local follow relationships for hygiene.
        setFollowStatus({ following: false, follows_me: false });
        toast.success(`Blocked @${username}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Block action failed'); }
    finally { setBusy(false); }
  };
  const startDM = async () => {
    try {
      const target = data?.user;
      if (!target) return;
      const { data: conv } = await api.post('/conversations', { participant_id: target.id });
      nav(`/messages/${conv.id}`);
    } catch (e) { toast.error('Could not start DM'); }
  };

  if (!data) return <div className="mx-auto max-w-[760px] px-4 py-6 font-mono text-xs text-muted-foreground">Loading…</div>;
  const u = data.user || {};
  const myRoles = user?.roles || [];
  const badgeTotal = Object.values(u.badge_summary || {}).reduce((a, b) => a + b, 0);

  let friendBtn = null;
  if (!isMe) {
    if (friendStatus.status === 'accepted') friendBtn = <Button variant="outline" onClick={friendAction} disabled={busy} data-testid="profile-remove-friend"><UserCheck className="mr-1 h-4 w-4" /> Friends</Button>;
    else if (friendStatus.status === 'pending' && friendStatus.i_requested) friendBtn = <Button variant="outline" disabled data-testid="profile-friend-pending">Request sent</Button>;
    else if (friendStatus.status === 'pending' && !friendStatus.i_requested) friendBtn = <Button onClick={friendAction} disabled={busy} data-testid="profile-accept-friend">Accept friend request</Button>;
    else friendBtn = <Button variant="outline" onClick={friendAction} disabled={busy} data-testid="profile-add-friend"><UserPlus className="mr-1 h-4 w-4" /> Add friend</Button>;
  }

  return (
    <ProfileBackground customization={customization} wallMode>
      <div className="mx-auto flex h-full w-full max-w-[820px] flex-col px-4" data-testid="profile-page">
        {/* Static header section — never scrolls. */}
        <div className="shrink-0 pt-4">
        {previewMode && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-[hsl(var(--gold)/0.45)] bg-[hsl(var(--accent)/0.06)] px-3 py-2 backdrop-blur" data-testid="profile-preview-banner">
            <div className="flex items-center gap-2 text-sm">
              <span className="grid h-6 w-6 place-items-center rounded-full bg-[hsl(var(--accent)/0.15)] feed-accent-text">👁</span>
              <span><b className="font-display">Preview mode</b> · you're viewing your <span className="font-mono">{wall}</span> profile exactly as a visitor would.</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => nav(`/profile/edit?scope=${wall}`)}
              data-testid="profile-preview-exit"
            >
              Exit preview
            </Button>
          </div>
        )}
        <div className="rounded-[var(--radius)] border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))]/80 p-4 backdrop-blur shadow-[var(--shadow-elev-1)]">
          <div className="flex flex-wrap items-center gap-4">
            <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-full border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card-2))] font-mono" style={customization?.accent_color ? { boxShadow: `0 0 0 2px ${customization.accent_color}55` } : {}}>
              {u.avatar_file_id ? <img src={fileUrl(u.avatar_file_id)} alt="" className="h-full w-full object-cover" /> : (u.username || '?').slice(0,2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-3xl tracking-tight" data-testid="profile-display-name">{u.display_name || u.username}</div>
              <div className="font-mono text-xs text-muted-foreground" data-testid="profile-username">@{u.username}</div>
              <div className="mt-1 flex flex-wrap gap-1">{(u.roles || []).map((r) => <span key={r} className="rounded-full border border-[hsl(var(--app-border))] px-2 py-0.5 font-mono text-[11px]">{r}</span>)}</div>
              <div className="mt-2 flex flex-wrap items-center gap-3 font-mono text-[11px] text-muted-foreground">
                <span data-testid="profile-followers-count"><b className="text-foreground">{counts.followers}</b> followers</span>
                <span data-testid="profile-following-count"><b className="text-foreground">{counts.following}</b> following</span>
                <span><b className="text-foreground">{badgeTotal}</b> badges</span>
              </div>
            </div>
            <div className="mt-3 w-full sm:ml-auto sm:mt-0 sm:w-auto sm:flex sm:flex-col sm:gap-2" data-testid="profile-actions">
              {showAsOwner ? (
                <>
                  {/* Mobile: 3 primary actions in a horizontal grid that wraps if needed.
                      Desktop: preserve the original vertical stack. */}
                  <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-col">
                    <Button onClick={() => nav('/profile/edit')} data-testid="profile-edit-button" className="justify-center"><Cog className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">Customize profile</span><span className="sm:hidden">Edit</span></Button>
                    <Button variant="outline" onClick={() => nav('/library')} data-testid="profile-library-button" className="justify-center"><Folder className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">My library</span><span className="sm:hidden">Library</span></Button>
                    <Button variant="outline" onClick={() => nav('/friends')} data-testid="profile-friends-button" className="justify-center"><Users className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">My friends</span><span className="sm:hidden">Friends</span></Button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 sm:mt-0">
                    {!myRoles.includes('CREATOR') && <Button size="sm" variant="outline" onClick={() => requestRole('CREATOR')} disabled={busy} data-testid="profile-request-creator">Become Creator</Button>}
                    {!myRoles.includes('CROWDFUNDER') && <Button size="sm" variant="outline" onClick={() => requestRole('CROWDFUNDER')} disabled={busy} data-testid="profile-request-crowdfunder">Become Crowdfunder</Button>}
                    {!myRoles.includes('INVESTOR') && <Button size="sm" variant="outline" onClick={() => requestRole('INVESTOR')} disabled={busy} data-testid="profile-request-investor">Become Investor</Button>}
                  </div>
                </>
              ) : !isMe ? (
                <div className="flex flex-col gap-2">
                  {blockState.they_blocked_me ? (
                    <div className="rounded-xl border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card-2))] p-2 text-center font-mono text-[11px] text-muted-foreground" data-testid="profile-blocked-by-them">
                      <ShieldAlert className="mx-auto mb-1 h-4 w-4 text-[hsl(var(--danger))]" />
                      You can't interact with this profile.
                    </div>
                  ) : blockState.i_blocked_them ? (
                    <>
                      <div className="rounded-xl border border-[hsl(var(--danger)/0.4)] bg-[hsl(var(--danger)/0.10)] p-2 text-center font-mono text-[11px] text-[hsl(var(--danger))]" data-testid="profile-blocked-by-me">
                        <Ban className="mx-auto mb-1 h-4 w-4" /> You blocked @{username}
                      </div>
                      <Button variant="outline" onClick={toggleBlock} disabled={busy} data-testid="profile-unblock-button">
                        <Ban className="mr-1 h-4 w-4" /> Unblock
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={toggleFollow} disabled={busy} data-testid={followStatus.following ? "profile-unfollow-button" : "profile-follow-button"}>
                        {followStatus.following ? <UserMinus className="mr-1 h-4 w-4" /> : <UserPlus className="mr-1 h-4 w-4" />}
                        {followStatus.following ? 'Unfollow' : (followStatus.follows_me ? 'Follow back' : 'Follow')}
                      </Button>
                      {friendBtn}
                      <Button variant="outline" onClick={startDM} data-testid="profile-dm-button"><MessageCircle className="mr-1 h-4 w-4" /> Message</Button>
                      <Button variant="ghost" size="sm" onClick={toggleBlock} disabled={busy} className="text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger)/0.10)]" data-testid="profile-block-button">
                        <Ban className="mr-1 h-4 w-4" /> Block
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => nav(`/u/${username}/library`)} data-testid="profile-view-library-button">
                        <Folder className="mr-1 h-4 w-4" /> View library
                      </Button>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          </div>
          {customization?.bio_html && <div className="prose prose-invert mt-3 max-w-none text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: sanitizeBioHtml(customization.bio_html) }} />}
          <div className="mt-3 flex items-center gap-3 font-mono text-[11px] text-muted-foreground">
            {Object.entries(u.badge_summary || {}).map(([t, n]) => <span key={t} className="rounded-full border border-[hsl(var(--app-border))] px-2 py-0.5">${t} × {n}</span>)}
          </div>
        </div>
        </div>
        {/* End static header. The Tabs below own the rest of the height
            with their own internal scroll so the page itself stays anchored. */}

        <Tabs value={tab} onValueChange={setTab} className="mt-4 flex flex-1 flex-col overflow-hidden">
          <TabsList className="shrink-0">
            <TabsTrigger value="posts" data-testid="profile-tab-posts">Posts</TabsTrigger>
            <TabsTrigger value="followers" data-testid="profile-tab-followers">Followers</TabsTrigger>
            <TabsTrigger value="following" data-testid="profile-tab-following">Following</TabsTrigger>
          </TabsList>
          <TabsContent value="posts" className="mt-3 flex-1 overflow-y-auto pb-6" data-testid="profile-posts-scroll">
            <ProfileLibraryStrip username={username} isOwner={isMe} />
            <Tabs value={wall} onValueChange={setWall} data-testid="profile-wall-tabs">
              <TabsList className="flex w-full flex-wrap">{WALL_KINDS.map((k) => <TabsTrigger key={k} value={k} className="capitalize" data-testid={`profile-wall-${k}`}>{k}</TabsTrigger>)}</TabsList>
              {WALL_KINDS.map((k) => {
                const posts = postsByWall[k] || [];
                return (
                  <TabsContent key={k} value={k} className="space-y-3 pt-3" data-testid={`profile-wall-content-${k}`}>
                    {posts.map((p) => <FeedCard key={p.id} post={p} />)}
                    {posts.length === 0 && <div className="font-mono text-xs text-muted-foreground">Nothing on the {k} wall yet.</div>}
                  </TabsContent>
                );
              })}
            </Tabs>
            {showAsOwner && (
              <div className="mt-3 flex flex-wrap gap-2">
                {wall === 'dating' && <Button variant="outline" onClick={() => nav('/dating/profile')} data-testid="profile-edit-dating">Edit dating profile</Button>}
                <Button variant="outline" onClick={() => nav(`/profile/edit?scope=${wall}`)} data-testid={`profile-customize-${wall}`}><Cog className="mr-1 h-4 w-4" /> Customize {wall} page</Button>
              </div>
            )}
          </TabsContent>
          <TabsContent value="followers" className="mt-3 flex-1 overflow-y-auto pb-6" data-testid="profile-followers-list">
            {followers.length === 0 ? <div className="font-mono text-xs text-muted-foreground">No followers yet.</div> : (
              <div className="grid gap-2 md:grid-cols-2">
                {followers.map((f) => (
                  <Link key={f.user.id} to={`/u/${f.user.username}`} className="flex items-center gap-3 rounded-lg border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))] px-3 py-2">
                    <div className="grid h-9 w-9 place-items-center overflow-hidden rounded-full border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card-2))] text-xs font-mono">{f.user.avatar_file_id ? <img src={fileUrl(f.user.avatar_file_id)} alt="" className="h-full w-full object-cover" /> : (f.user.username || '?').slice(0,2).toUpperCase()}</div>
                    <div><div className="text-sm">{f.user.display_name || f.user.username}</div><div className="font-mono text-[11px] text-muted-foreground">@{f.user.username}</div></div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="following" className="mt-3 flex-1 overflow-y-auto pb-6" data-testid="profile-following-list">
            {following.length === 0 ? <div className="font-mono text-xs text-muted-foreground">Not following anyone yet.</div> : (
              <div className="grid gap-2 md:grid-cols-2">
                {following.map((f) => (
                  <Link key={f.user.id} to={`/u/${f.user.username}`} className="flex items-center gap-3 rounded-lg border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))] px-3 py-2">
                    <div className="grid h-9 w-9 place-items-center overflow-hidden rounded-full border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card-2))] text-xs font-mono">{f.user.avatar_file_id ? <img src={fileUrl(f.user.avatar_file_id)} alt="" className="h-full w-full object-cover" /> : (f.user.username || '?').slice(0,2).toUpperCase()}</div>
                    <div><div className="text-sm">{f.user.display_name || f.user.username}</div><div className="font-mono text-[11px] text-muted-foreground">@{f.user.username}</div></div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <AudioPlayer playlist={customization?.playlist || []} autoplay={!!customization?.audio_autoplay} loop={customization?.audio_loop !== false} accent={customization?.accent_color} />
    </ProfileBackground>
  );
}
